import React from 'react';
import { ProgramLink, ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const DivisionChristianCounseling = () => {
  const programs = [
    {
      title: "Master of Arts in Christian Counseling and Psychology",
      description: "Advanced study combining psychological principles with Christian faith for holistic counseling practice.",
      path: "/academics/ma_christian_counseling_and_psychology",
      icon: "📚"
    }
  ];

  const features = [
    {
      title: "Faith Integration",
      description: "Integration of Christian principles with psychological theory",
      icon: "🤝"
    },
    {
      title: "Professional Development",
      description: "Ongoing training and certification opportunities",
      icon: "📈"
    },
    {
      title: "Community Impact",
      description: "Preparation for church-based and independent counseling ministry",
      icon: "🌟"
    },
    {
      title: "Ethical Practice",
      description: "Strong foundation in counseling ethics and professional standards",
      icon: "⚖️"
    }
  ];

  const resources = [
    {
      title: "Professional Review Board",
      description: "Information about certification and continuing education",
      path: "/academics/counseling/prb"
    },
    {
      title: "Clinical Training",
      description: "Details about practicum and supervision requirements",
      path: "/academics/counseling/clinical"
    },
    {
      title: "Ministry Integration",
      description: "Resources for church-based counseling ministry",
      path: "/academics/counseling/ministry"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Division of Christian Counseling and Psychology
            </h1>
            <p className="text-xl opacity-90">
              Integrating faith and psychology for holistic counseling practice
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">About Our Division</h2>
              <p className="text-gray-600 mb-4">
                The Division of Christian Counseling and Psychology provides comprehensive 
                training for understanding human behavior and helping those who are hurting. 
                Our programs integrate Christian principles with psychological theory to 
                prepare counselors for effective ministry and practice.
              </p>
              <p className="text-gray-600">
                Graduates are prepared for both church-based counseling ministry and 
                independent practice, with strong support from professional review boards 
                and continuing education opportunities.
              </p>
            </div>
          </section>

          {/* Features Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Division Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Programs Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Available Programs</h2>
            <div className="space-y-4">
              {programs.map((program, index) => (
                <ProgramLink 
                  key={index}
                  title={program.title}
                  description={program.description}
                  path={program.path}
                  icon={program.icon}
                />
              ))}
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Professional Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Counseling Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of counseling professionals and make a difference in people's lives.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DivisionChristianCounseling; 